import type { CommunitySwitcher } from "@circle-react/types/CommunitySwitcher";
import { resolveThemeColor } from "@circleco/community-switcher/helpers/bgColor";
import { communitySwitcherInitials } from "@circleco/community-switcher/helpers/utils";

/**
 * Returns true if a hex color is perceptually light (should use dark text).
 * Matches the luminance threshold used in the desktop community switcher
 * (see shared/CommunityLogo/WithName.tsx).
 */
const isLightColor: (hex: string) => boolean = hex => {
  const cleaned = hex.replace("#", "");
  if (cleaned.length < 6) return false;
  const r = parseInt(cleaned.slice(0, 2), 16) / 255;
  const g = parseInt(cleaned.slice(2, 4), 16) / 255;
  const b = parseInt(cleaned.slice(4, 6), 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.65;
};

interface CommunityAvatarProps {
  community: CommunitySwitcher;
  isDarkAppearance: boolean;
  size?: "sm" | "md";
}

/**
 * Community avatar showing initials on a brand-colored background.
 * Matches the desktop sidebar community switcher style.
 */
export const CommunityAvatar = ({
  community,
  isDarkAppearance,
  size = "md",
}: CommunityAvatarProps) => {
  const backgroundColor = resolveThemeColor(
    community.light_brand_color,
    community.dark_brand_color,
    isDarkAppearance,
  );
  const sizeClass = size === "sm" ? "size-8" : "size-10";
  const textColor =
    backgroundColor && isLightColor(backgroundColor)
      ? "text-zinc-800"
      : "text-white";

  return (
    <span
      aria-hidden
      className={`flex ${sizeClass} shrink-0 select-none items-center justify-center overflow-hidden rounded-lg bg-zinc-800 text-sm font-bold leading-none ${textColor}`}
      style={backgroundColor ? { backgroundColor } : undefined}
    >
      {communitySwitcherInitials(community.name)}
    </span>
  );
};
